import { UzButton, UzConfirmDialog, UzDialog, UzDropdownMenu, UzSpinner } from '@utazzo/ui';
import { Download, MoreHorizontal, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  downloadCsv,
  downloadExcel,
  downloadPdf,
  excludeRows,
  fetchAllPages,
} from './list-screen-utils';
import { useListScreen } from './use-list-screen';

import type { ListScreenProps } from './types';
import type { DataGridBulkAction, DataGridColumn } from '@/components/data-grid';
import type { UzDropdownMenuItem } from '@utazzo/ui';

import { DataGrid } from '@/components/data-grid';
import { PageShell } from '@/components/page-shell';
import { useCanExportData } from '@/features/auth/use-auth';

/**
 * Above this many matching rows, the export dialog shows a "large export"
 * warning. The export pages the server MAX_PAGE_SIZE rows at a time
 * (sequential requests) and builds the whole file in browser memory, so a
 * very large unfiltered export can be slow / heavy.
 */
const EXPORT_CONFIRM_THRESHOLD = 10_000;

/** One filter summary pill in the export dialog. */
const ExportFilterChip = ({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element => (
  <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs">
    <span className="shrink-0 text-zinc-500">{label}:</span>
    <span className="truncate font-medium text-zinc-700">{value}</span>
  </span>
);

export function ListScreen<TRow, TQuery>(props: ListScreenProps<TRow, TQuery>): React.JSX.Element {
  const screen = useListScreen(props);
  const { gridState, drawer, deletion } = screen;
  // Bulk data export is ADMIN-only by default (see `useCanExportData`). A screen
  // can widen this by passing `export.canExport` (e.g. finance listings let the
  // FINANCE role export too via `useCanExportFinanceData`). Both of this screen's
  // download paths - the toolbar button and the "Export selected" bulk action -
  // hang off this one flag.
  const canExportDefault = useCanExportData();
  const canExport = props.export?.canExport ?? canExportDefault;

  // State for the set-active / set-inactive confirm dialog.
  const [setActiveTarget, setSetActiveTarget] = useState<{
    rows: TRow[];
    isActive: boolean;
  } | null>(null);
  // Export dialog: opens on "Export", shows the active filters + row count,
  // then a loading screen while it pages through the server.
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const {
    title,
    description,
    gridId,
    createButton,
    headerExtra,
    filtersExtra,
    embedded,
    tableLayout,
    columns,
    filters,
    getRowId,
    onRowClick,
    disableRowClick,
    renderRowExtras,
    renderMobileCard,
    overlays,
    renderDrawer,
    rowActions,
    bulkActions,
    export: exportConfig,
    copy,
  } = props;

  // Resolve columns (static or state-aware) and append a generated actions column.
  const resolvedColumns =
    typeof columns === 'function' ? columns({ state: gridState.state }) : columns;

  const actionsColumn: DataGridColumn<TRow> | null =
    rowActions && (rowActions.edit || rowActions.extra || rowActions.delete)
      ? {
          id: 'actions',
          header: '',
          align: 'right',
          width: '64px',
          alwaysVisible: true,
          truncate: false,
          resizable: false,
          cell: (row) => {
            // Items are concatenated in order: Edit, then the page's own
            // `extra(row)` items, then Delete. ListScreen does NOT inject any
            // separators - a page controls grouping by placing `{ type:
            // 'separator' }` entries inside its `extra` array.
            const items: UzDropdownMenuItem[] = [];
            if (rowActions.edit && renderDrawer) {
              items.push({
                type: 'item',
                label: 'Edit',
                icon: <Pencil className="h-4 w-4" />,
                onSelect: () => drawer.openEdit(row),
              });
            }
            items.push(...(rowActions.extra?.(row) ?? []));
            if (rowActions.delete) {
              items.push({
                type: 'item',
                label: 'Delete',
                icon: <Trash2 className="h-4 w-4" />,
                danger: true,
                disabled: rowActions.delete.disabled?.(row) ?? false,
                onSelect: () => deletion.request(row),
              });
            }
            return (
              <UzDropdownMenu
                align="end"
                trigger={
                  <UzButton
                    size="sm"
                    variant="ghost"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Row actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </UzButton>
                }
                items={items}
              />
            );
          },
        }
      : null;

  const allColumns = actionsColumn ? [...resolvedColumns, actionsColumn] : resolvedColumns;

  // Helper to trigger a download in the requested format (defaults to the
  // config's format for the toolbar/bulk paths; the dialog passes 'pdf' explicitly).
  const runExport = (rows: ReadonlyArray<TRow>, fmt?: 'xlsx' | 'csv' | 'pdf'): void => {
    if (!exportConfig) return;
    const format = fmt ?? exportConfig.format ?? 'xlsx';
    if (format === 'pdf') {
      downloadPdf(
        rows,
        exportConfig.csv,
        exportConfig.filename,
        typeof title === 'string' ? title : undefined,
      );
    } else if (format === 'csv') {
      downloadCsv(rows, exportConfig.csv, exportConfig.filename);
    } else {
      downloadExcel(rows, exportConfig.csv, exportConfig.filename);
    }
  };

  // Assemble bulk actions: Export selected (if export configured) + Set active +
  // Set inactive + extra + Delete. Order: Set active, Set inactive, Delete (per spec).
  //
  // 2026-07-23. "Export selected" is ADMIN-only, same rule as the toolbar's
  // export button. It is a SECOND download path (selection bar, not toolbar),
  // so it needs its own check - gating the toolbar alone would leave
  // non-admins able to tick rows and export them from the selection bar.
  // When export is a page's only bulk action this also drops the row
  // checkboxes for non-admins, which is right: there is nothing left to do
  // with a selection.
  const gridBulkActions: DataGridBulkAction<TRow>[] = [];
  if (exportConfig && canExport) {
    const fmt = exportConfig.format ?? 'xlsx';
    gridBulkActions.push({
      id: 'export',
      label: fmt === 'xlsx' ? 'Export Excel' : 'Export CSV',
      icon: <Download className="h-3.5 w-3.5" />,
      onAction: (_ids, rows) => runExport(rows),
    });
  }
  if (bulkActions?.setActive) {
    gridBulkActions.push({
      id: 'set-active',
      label: 'Set active',
      onAction: (_ids, rows) => setSetActiveTarget({ rows, isActive: true }),
    });
    gridBulkActions.push({
      id: 'set-inactive',
      label: 'Set inactive',
      onAction: (_ids, rows) => setSetActiveTarget({ rows, isActive: false }),
    });
  }
  if (bulkActions?.extra) gridBulkActions.push(...bulkActions.extra);
  if (bulkActions?.delete) {
    gridBulkActions.push({
      id: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-3.5 w-3.5" />,
      danger: true,
      onAction: (_ids, rows) => deletion.requestBulk(rows),
    });
  }
  const selectable = gridBulkActions.length > 0;

  const exportTotal = screen.pagination?.totalItems ?? 0;
  const runExportAll = async (fmt?: 'xlsx' | 'csv' | 'pdf'): Promise<void> => {
    if (!exportConfig) return;
    setExporting(true);
    try {
      const all = await fetchAllPages(exportConfig.fetchPage, screen.query);
      runExport(all, fmt);
      setExportOpen(false);
    } catch (err) {
      // A paged fetch that fails part-way (a 400 from a list that caps
      // pageSize below the export's, a network drop) used to reject a
      // `void`-ed promise: the spinner stopped and nothing else happened.
      // The operator must be told the file did not download.
      toast.error('Export failed', {
        description: err instanceof Error ? err.message : 'Could not fetch the rows to export.',
      });
    } finally {
      setExporting(false);
    }
  };

  // "Export" just opens the dialog now - the operator reviews the filters +
  // row count there, then triggers the actual paged fetch from inside it.
  const handleExportAll =
    exportConfig && canExport
      ? async (): Promise<void> => {
          setExportOpen(true);
        }
      : undefined;

  // Human-readable summary of what will be exported: page-supplied context
  // rows (e.g. a URL-level date range the grid filters don't capture) + the
  // grid's own active filters + the search term.
  const exportFilterChips: Array<{ label: string; value: string }> = [];
  for (const f of filters ?? []) {
    const v = gridState.state.filters[f.id];
    if (v === undefined || v === null || v === '') continue;
    const opt = f.options.find((o) => o.value === v);
    exportFilterChips.push({ label: f.label, value: opt?.label ?? String(v) });
  }
  const exportSearch = gridState.state.q?.trim() ?? '';
  const exportContextRows = exportConfig?.contextRows ?? [];
  const exportSummaryCount =
    exportContextRows.length + exportFilterChips.length + (exportSearch ? 1 : 0);

  // Resolution order: explicit `onRowClick` wins, then `disableRowClick`
  // (no-op), then the default "open edit drawer" behaviour when a
  // drawer is configured.
  const effectiveRowClick = disableRowClick
    ? undefined
    : (onRowClick ?? (renderDrawer ? (row: TRow) => drawer.openEdit(row) : undefined));

  const body = (
    <>
      <DataGrid<TRow>
        filtersExtra={filtersExtra}
        columns={allColumns}
        data={screen.data}
        pagination={screen.pagination}
        isLoading={screen.list.isLoading}
        isError={screen.list.isError}
        error={{ title: copy?.errorTitle, description: copy?.errorDescription }}
        onRetry={() => void screen.list.refetch()}
        state={gridState.state}
        onStateChange={{
          setSearch: gridState.setSearch,
          setFilter: gridState.setFilter,
          setSort: gridState.setSort,
          setPage: gridState.setPage,
          setPageSize: gridState.setPageSize,
          setDensity: gridState.setDensity,
          setHidden: gridState.setHidden,
        }}
        filters={filters}
        selectable={selectable}
        bulkActions={selectable ? gridBulkActions : undefined}
        onExport={handleExportAll}
        canExportOverride={canExport}
        exportLabel={
          exportConfig
            ? (exportConfig.format ?? 'xlsx') === 'xlsx'
              ? 'Export Excel'
              : 'Export CSV'
            : undefined
        }
        getRowId={(row) => getRowId(row)}
        renderRowExtras={renderRowExtras}
        renderMobileCard={renderMobileCard}
        gridId={gridId}
        tableLayout={tableLayout}
        onRowClick={effectiveRowClick}
        searchPlaceholder={copy?.searchPlaceholder}
        emptyTitle={copy?.emptyTitle}
        emptyDescription={copy?.emptyDescription}
      />

      {renderDrawer?.({
        open: drawer.mode !== null,
        mode: drawer.mode ?? 'create',
        row: drawer.row,
        close: drawer.close,
      })}

      {rowActions?.delete && (
        <UzConfirmDialog
          open={deletion.target !== null}
          onOpenChange={(open) => {
            if (!open) deletion.clear();
          }}
          title={deletion.target ? rowActions.delete.confirm(deletion.target).title : ''}
          description={
            deletion.target ? rowActions.delete.confirm(deletion.target).description : ''
          }
          confirmLabel="Delete"
          tone="destructive"
          onConfirm={async () => {
            if (deletion.target) {
              await rowActions.delete!.onDelete(deletion.target);
              deletion.clear();
            }
          }}
        />
      )}

      {bulkActions?.delete && (
        <UzConfirmDialog
          open={deletion.targets !== null}
          onOpenChange={(open) => {
            if (!open) deletion.clearBulk();
          }}
          title={deletion.targets ? bulkActions.delete.confirm(deletion.targets).title : ''}
          description={
            deletion.targets ? bulkActions.delete.confirm(deletion.targets).description : ''
          }
          confirmLabel="Delete"
          tone="destructive"
          onConfirm={async () => {
            if (!deletion.targets) return;
            const rows = excludeRows(deletion.targets, bulkActions.delete!.excludeRow);
            if (rows.length > 0) await bulkActions.delete!.onDelete(rows);
            deletion.clearBulk();
          }}
        />
      )}

      {bulkActions?.setActive && setActiveTarget && (
        <UzConfirmDialog
          open={setActiveTarget !== null}
          onOpenChange={(open) => {
            if (!open) setSetActiveTarget(null);
          }}
          title={
            bulkActions.setActive.confirm(setActiveTarget.rows, setActiveTarget.isActive).title
          }
          description={
            bulkActions.setActive.confirm(setActiveTarget.rows, setActiveTarget.isActive)
              .description
          }
          confirmLabel={setActiveTarget.isActive ? 'Set active' : 'Set inactive'}
          tone="primary"
          onConfirm={async () => {
            if (!setActiveTarget) return;
            const rows = excludeRows(setActiveTarget.rows, bulkActions.setActive!.excludeRow);
            if (rows.length > 0) {
              const res = await bulkActions.setActive!.onSetActive(rows, setActiveTarget.isActive);
              if (res.failed.length > 0) {
                toast.warning(`Updated ${res.updated}; ${res.failed.length} skipped`);
              } else {
                toast.success(`Updated ${res.updated}`);
              }
            }
            setSetActiveTarget(null);
          }}
        />
      )}
      {exportConfig && (
        <UzDialog
          open={exportOpen}
          onOpenChange={(o) => {
            // Can't dismiss mid-export - forceOpen locks it while fetching.
            if (!exporting) setExportOpen(o);
          }}
          forceOpen={exporting}
          size="md"
          title="Export"
          description={
            exporting
              ? undefined
              : `${exportTotal.toLocaleString()} row${exportTotal === 1 ? '' : 's'} match the current filters. Choose a format.`
          }
          footer={
            exporting ? undefined : (
              <>
                <UzButton variant="ghost" onClick={() => setExportOpen(false)}>
                  Cancel
                </UzButton>
                <UzButton
                  variant="outline"
                  onClick={() => void runExportAll('pdf')}
                  disabled={exportTotal === 0}
                >
                  <Download className="mr-2 h-4 w-4" />
                  PDF
                </UzButton>
                <UzButton onClick={() => void runExportAll('xlsx')} disabled={exportTotal === 0}>
                  <Download className="mr-2 h-4 w-4" />
                  Excel
                </UzButton>
              </>
            )
          }
        >
          {exporting ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <UzSpinner />
              <p className="text-sm font-medium text-zinc-700">Preparing your Excel file…</p>
              <p className="text-xs text-zinc-500">
                Fetching {exportTotal.toLocaleString()} rows in batches - this can take a moment.
              </p>
            </div>
          ) : (
            <div className="space-y-3 py-1">
              <div>
                <div className="text-xs font-medium tracking-wide text-zinc-500 uppercase">
                  Filters applied
                </div>
                {exportSummaryCount === 0 ? (
                  <p className="mt-1.5 text-sm text-zinc-500">
                    No filters. Exporting the full list.
                  </p>
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {exportContextRows.map((r) => (
                      <ExportFilterChip key={`ctx-${r.label}`} label={r.label} value={r.value} />
                    ))}
                    {exportFilterChips.map((c) => (
                      <ExportFilterChip key={`f-${c.label}`} label={c.label} value={c.value} />
                    ))}
                    {exportSearch && <ExportFilterChip label="Search" value={exportSearch} />}
                  </div>
                )}
              </div>
              {exportTotal > EXPORT_CONFIRM_THRESHOLD && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
                  Large export ({exportTotal.toLocaleString()} rows). It runs many sequential
                  requests and builds the file in memory, so it may be slow. Consider narrowing the
                  filters first.
                </p>
              )}
            </div>
          )}
        </UzDialog>
      )}
      {overlays}
    </>
  );

  // Embedded mode skips the page header - the host page owns it (e.g. the
  // Lead inbox tabs share a single "Lead inbox" header across tabs).
  if (embedded) {
    return <div className="space-y-6">{body}</div>;
  }

  return (
    <PageShell
      title={title}
      description={description}
      actions={
        <>
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => void screen.list.refetch()}
            disabled={screen.list.isFetching}
            aria-label="Refresh"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${screen.list.isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </UzButton>
          {createButton && (createButton.onClick || renderDrawer) && (
            <UzButton onClick={createButton.onClick ?? (() => drawer.openCreate())}>
              <Plus className="mr-2 h-4 w-4" />
              {createButton.label}
            </UzButton>
          )}
          {headerExtra}
        </>
      }
    >
      {body}
    </PageShell>
  );
}
